import { computed, DestroyRef, Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { AuthorizationService } from '../../services/authorization/authorization.service';
import { AuthorizationStateService } from '../../services/authorization/authorization-state.service';
import { UserSessionService } from '../../services/user-session.service';
import { PermissionDirectiveOptions } from '../../models/ui/permission-directive-options.model';
import { PermissionUtilities } from '../../utilities/permission.utilities';
import { ProfileOrganizationScopeService } from '../../services/authorization/profile-organization-scope.service';
import { IfHasChildPermissionDirective } from '../if-has-child-permission/if-has-child-permission.directive';

@Directive({
	selector: '[ifHasPermission]',
	standalone: true
})
export class IfHasPermissionDirective {
	private readonly templateRef = inject(TemplateRef<unknown>);
	private readonly viewContainer = inject(ViewContainerRef);
	private readonly authorizationService = inject(AuthorizationService);
	private readonly authorizationStateService = inject(AuthorizationStateService);
	private readonly userSessionService = inject(UserSessionService);
	private readonly parent = inject(IfHasChildPermissionDirective, { optional: true, skipSelf: true });
	// Provided by an org-scoped profile page (organization/member/property) so permissions resolve
	// against the organization on screen rather than the active one; null everywhere else.
	private readonly profileScope = inject(ProfileOrganizationScopeService, { optional: true });

	public readonly ifHasPermission = input.required<PermissionDirectiveOptions>();

	private hasView = false;

	private readonly shouldRender = computed(() => PermissionUtilities.shouldOptionsRender(
		this.ifHasPermission(),
		this.authorizationService,
		this.authorizationStateService,
		this.userSessionService,
		this.profileScope
	));

	constructor() {
		const destroyRef = inject(DestroyRef);

		effect(() => {
			const shouldRender = this.shouldRender();

			if (shouldRender && !this.hasView) {
				this.viewContainer.createEmbeddedView(this.templateRef);
				this.hasView = true;
			} else if (!shouldRender && this.hasView) {
				this.viewContainer.clear();
				this.hasView = false;
			}
		});

		if (this.parent) {
			const parent = this.parent;
			// The parent is handed the same computed the render decision uses rather than a second
			// copy of the expression, so "is this rendered?" and "does the parent stay visible?" can't
			// drift apart.
			parent.register(this.shouldRender);
			destroyRef.onDestroy(() => parent.unregister(this.shouldRender));
		}
	}

}
